import { useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Menu, Moon, Sun } from "lucide-react";
import { Sidebar, type NavGroup } from "./Sidebar";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/cn";

/**
 * Shared chrome for the patient portal and the admin panel. The rail is fixed
 * from lg up and an overlay below it; content sits in a max-width column so
 * tables and vitals do not stretch to 2000px on a clinic monitor.
 */
/**
 * Routes that manage their own scrolling and must fill the viewport exactly.
 * The chat composer has to stay pinned on screen; in the normal document flow
 * it was pushed below the fold as the thread grew.
 */
const FULL_HEIGHT_ROUTES = ["/patient/chat", "/admin/chatbot"];

export function AppShell({
  groups,
  brand,
  sidebarFooter,
  topbarActions,
  children,
}: {
  groups: NavGroup[];
  brand: ReactNode;
  sidebarFooter?: ReactNode;
  topbarActions?: ReactNode;
  children: ReactNode;
}) {
  const [navOpen, setNavOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();
  const fullHeight = FULL_HEIGHT_ROUTES.includes(pathname);

  return (
    <div className={cn(fullHeight ? "h-dvh overflow-hidden" : "min-h-dvh")}>
      <Sidebar
        groups={groups}
        brand={brand}
        footer={sidebarFooter}
        open={navOpen}
        onClose={() => setNavOpen(false)}
      />

      <div className={cn("lg:pl-[260px]", fullHeight && "flex h-dvh flex-col")}>
        <header
          className={cn(
            "bg-canvas/85 border-hairline sticky top-0 z-20 border-b backdrop-blur-md",
            "flex h-14 items-center gap-2 px-4 sm:px-6",
          )}
        >
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Buka navigasi"
            aria-expanded={navOpen}
            className="hover:bg-surface-sunken -ml-1 grid size-9 place-items-center rounded-lg lg:hidden"
          >
            <Menu className="size-5" />
          </button>

          <div className="flex-1" />

          {topbarActions}

          <button
            type="button"
            onClick={toggle}
            aria-label={theme === "dark" ? "Gunakan tema terang" : "Gunakan tema gelap"}
            className="hover:bg-surface-sunken text-content-muted hover:text-content grid size-9 place-items-center rounded-lg"
          >
            {theme === "dark" ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
          </button>
        </header>

        <main
          className={cn(
            fullHeight
              // min-h-0 is what lets the thread scroll inside instead of
              // stretching the flex parent.
              ? "min-h-0 flex-1"
              : "mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 sm:py-8",
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * Page heading. Every screen uses this so the eyebrow → title → description
 * rhythm is identical across all 39 pages.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
}: {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between",
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow mb-1.5">{eyebrow}</p>}
        <h1 className="text-2xl sm:text-3xl">{title}</h1>
        {description && (
          <p className="text-content-muted mt-1.5 max-w-2xl text-sm">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
